/**
 * export.ts — DiagnosticsExport assembly (native-diagnostics-validation §8).
 *
 * Bundles the raw snapshot time-series and their validation reports into the
 * portable `DiagnosticsExport` format the dev panel / CI artifact share. The
 * builder stamps `thresholdVersion` so an export stays traceable to the exact
 * threshold set it was judged against (spec §7: thresholds are tuned per device
 * and versioned). Pure — no clock, no IO — so the dev panel only does the blob
 * download and vitest/CI build the same payload.
 */

import { THRESHOLD_VERSION } from './thresholds';

import type { DiagnosticsValidationReport } from './validation';
import type { DiagnosticsSnapshot } from '../types';

export type DiagnosticsExportDevice = NonNullable<
  DiagnosticsValidationReport['device']
>;

export type DiagnosticsExport = {
  schemaVersion: 1;
  thresholdVersion: string;
  startedAtMs: number;
  endedAtMs: number;
  device: DiagnosticsExportDevice;
  snapshots: DiagnosticsSnapshot[];
  reports: DiagnosticsValidationReport[];
};

export type BuildDiagnosticsExportInput = {
  snapshots: DiagnosticsSnapshot[];
  reports: DiagnosticsValidationReport[];
  device: DiagnosticsExportDevice;
};

/**
 * Assemble a `DiagnosticsExport` from a snapshot/report buffer. `startedAtMs`/
 * `endedAtMs` derive from the first/last snapshot timestamps (0 when empty), and
 * the active `THRESHOLD_VERSION` is stamped so threshold changes are tracked.
 */
export const buildDiagnosticsExport = (
  input: BuildDiagnosticsExportInput,
): DiagnosticsExport => {
  const { snapshots, reports, device } = input;
  const last = snapshots[snapshots.length - 1];
  return {
    schemaVersion: 1,
    thresholdVersion: THRESHOLD_VERSION,
    startedAtMs: snapshots[0]?.timestampMs ?? 0,
    endedAtMs: last?.timestampMs ?? 0,
    device,
    snapshots,
    reports,
  };
};
